// settingsHelp.ts — the on-screen explanation of every provisioning-default
// setting, in one reviewable place.
//
// THE RULE THIS FILE IS WRITTEN UNDER: every sentence below was derived from the
// code that CONSUMES the setting, not from the field's name, not from an existing
// label, not from a comment, and not from a design document. Where the consuming
// code was found, the file and line is cited in a `// src:` comment beside the
// entry so a reviewer can re-check it rather than trust it. Where no consumer
// exists, the entry says so in `status` and the on-screen text says so too — a
// confident wrong sentence on a settings screen is an instruction someone
// configures a live system by.
//
// Two comments in the codebase are OUT OF DATE and were deliberately NOT used as
// sources here (both were checked against the code and the code disagrees):
//   - migrations/063_draft_upload_rules.sql:29 says notify_on_transition is
//     "reserved for future flexibility; today every label notifies". It is read
//     and honoured at utils/draftStatusHandler.js:180.
//   - utils/passwordPolicy.js:3 says the module is "not imported anywhere". It is
//     imported by routes/auth.js:22, routes/invites.js:5, routes/_combined.js:9.
//
// The user-facing text carries no table names, no function names, no file paths
// and no status codes — the reader is a lawyer, not an engineer.

export type FieldStatus =
  /** Read at runtime by the customer's live system. */
  | "live"
  /** Read only when a customer is created; nothing reads it afterwards. */
  | "setup_only"
  /** Stored and displayed, but nothing anywhere acts on it. */
  | "inert"
  /** No consumer could be found in the code. */
  | "undetermined";

export interface FieldHelp {
  /** Plain-English name shown on screen. */
  label: string;
  /** One line, always visible. */
  summary: string;
  /** The fuller explanation, revealed on request. One string per paragraph. */
  detail: string[];
  /** For a value or an enum: what each possible value actually causes. */
  values?: { value: string; effect: string }[];
  /** The default, and why it is the default. */
  fallback: string;
  /** What else this setting changes the behaviour of. */
  interacts?: string;
  status: FieldStatus;
  /** Shown beside the status badge when the status is not plainly "live". */
  statusNote?: string;
}

export interface SectionHelp {
  label: string;
  /** One line, shown on the surface card and above the list. */
  summary: string;
  /** Shown above the field list. */
  detail: string[];
  fields: Record<string, FieldHelp>;
}

/** The phrase to use — verbatim — when the code does not answer the question. */
export const UNDETERMINED = "This setting's effect could not be determined from the code.";

// Shared wording for the two flags that appear on every list section, so the
// copy cannot drift between sections.

// src: services/provisioningDefaults.js:44 (`selected !== false`) and :289, where
// the filter is applied to every list section at customer-creation time. No other
// reader of the flag exists anywhere in the backend.
const SELECTED: FieldHelp = {
  label: "Include when a customer is created",
  summary: "Whether this entry is copied into a new customer. It changes nothing after that.",
  detail: [
    "When a new customer is set up, only the entries marked for inclusion are copied across. An excluded entry stays in this master list but the new customer never receives it, so nothing about it can ever apply to them.",
    "Once the customer exists, this has no further effect. It is not copied to them, and no part of their day-to-day system consults it.",
    "This is not the same thing as the active switch on the same row. Inclusion decides whether the customer is given the entry at all. Active decides whether the entry they were given does anything. An entry can be included and switched off, in which case the customer has it but it is dormant until someone turns it on.",
  ],
  fallback: "Included. An entry newly picked up from the source customer arrives included, and an entry with nothing recorded either way counts as included.",
  interacts:
    "No screen in this console can currently change it. The editor that used to set it is no longer reachable, so in practice every entry in this list is copied. It is shown here read-only so the value you are relying on is at least visible.",
  status: "setup_only",
  statusNote: "Read only while a customer is being created.",
};

export const SETTINGS_HELP: Record<string, SectionHelp> = {
  // ───────────────────────────────────────────────────────────────────────────
  routing_rules: {
    label: "Routing rules",
    summary:
      "For each type of agreement, which team is told about a new request and which queue it waits in.",
    detail: [
      "When someone submits a contract request, the system looks for the one rule whose agreement type matches the type on the request. If it finds one, it notifies that team and puts the request in that team's queue. If it finds none, nobody is notified and the request sits in the general unassigned queue until a person picks it up.",
      "Only one rule may exist per agreement type. Questions are never routed by these rules — they always wait for a human to pick them up.",
    ],
    fields: {
      // src: routes/requests.js:901 `WHERE agreement_type = $1` — plain equality,
      // no lowering, so the match is case- and punctuation-exact. Same shape at
      // requests.js:862, routes/selfService.js:1395. UNIQUE(agreement_type) at
      // migrations/027_routing_rules.sql:16.
      agreement_type: {
        label: "Agreement type",
        summary: "The type of agreement this rule applies to, matched exactly as written.",
        detail: [
          "When a request is submitted, its agreement type is compared with this value character for character. A difference in spelling, spacing, punctuation or capital letters means the rule does not match, no team is notified, and the request waits in the general unassigned queue.",
          "There can only be one rule per agreement type.",
        ],
        fallback: "No default — a rule without one cannot match anything.",
        interacts:
          "It must name an agreement type the customer actually has. When a customer is created, a rule naming a type that customer is not being given is skipped and reported as skipped, rather than copied.",
        status: "live",
      },

      // src: routes/requests.js:906-928 (team fan-out + status), :860-871
      // (duplicate-override fan-out), routes/selfService.js:1395-1420,
      // utils/draftStatusHandler.js:93 and :547 (commercial-ops branch),
      // routes/_combined.js:816 (commercial queue filter).
      // Default 'legal_ops' at migrations/027_routing_rules.sql:9 and
      // services/provisioningDefaults.js:389.
      primary_route: {
        label: "Owning team",
        summary: "Which team owns requests of this type: legal, or commercial.",
        detail: [
          "This is the setting on the row that does the most. It decides who is told, which queue the request waits in, which list it appears on, and — later in the life of the request — whether a returned counterparty draft goes to legal or to commercial for review.",
          "Only the two values below are recognised. Anything else behaves exactly as if legal had been chosen.",
        ],
        values: [
          {
            value: "Legal operations",
            effect:
              "The request waits in the legal assignment queue. Administrators and legal operations staff are notified. When the other side returns a draft, it goes to legal review.",
          },
          {
            value: "Commercial operations",
            effect:
              "The request waits in the commercial assignment queue. Administrators, legal operations, commercial operations and contract managers are all notified, and the request appears on the commercial worklist. When the other side returns a draft, it goes to commercial review instead of legal review — but only if the rule for counterparty drafts has its follow-the-team switch turned on.",
          },
        ],
        fallback:
          "Legal operations. It is the value used when the field is left blank, both by the database and by the copying step that sets up a new customer.",
        interacts:
          "The counterparty-draft upload rule, which can change a request's status depending on which team owns it. Also the wording and audience of the alert sent when someone submits a request that looks like a duplicate: commercial operations and contract managers for a commercial request, lawyers and legal operations for a legal one.",
        status: "live",
      },

      // src: routes/requests.js:918 — the ONLY behavioural read is the wording of
      // the in-app message (`Assign a ${rule.primary_role...}`). Also stored on
      // the request at :927, but every reader of that stored block reads only the
      // route (draftStatusHandler.js:93, reviewerNotifications.js:97,
      // _combined.js:816, frontend Dashboards.tsx:143).
      // services/provisioningDefaults.js:381-382 is the set-up-time effect.
      primary_role: {
        label: "Role named in the alert",
        summary:
          "The job title written into the message the team receives. It does not assign anybody.",
        detail: [
          "When a request of this type arrives, the team is sent a message ending \"assign a lawyer\", or whichever role is named here. That wording is the whole of its effect on the running system. Nothing reads it to choose a person, to decide who is allowed to take the request, or to change its status.",
          "It has one further effect, and only while a customer is being created: a rule that names no role at all — neither here nor in the second-team role — is dropped and not copied to the new customer. If this is blank but the second-team role is filled in, the second-team role is promoted into this position and the rule is copied with the second team's role removed.",
        ],
        fallback: "Lawyer.",
        interacts: "The second-team role, which stands in for this one if it is left blank.",
        status: "live",
        statusNote: "Live, but its only effect is the wording of a message.",
      },

      // src: written at routes/requests.js:927 and routes/selfService.js:1405 and
      // :1552; read nowhere. The four readers of the stored routing block
      // (draftStatusHandler.js:93, reviewerNotifications.js:97,
      // _combined.js:816, Dashboards.tsx:143) each read the primary route only.
      // Rendered in the old defaults editor at DefaultsEditor.tsx:48, which is no
      // longer reachable.
      secondary_route: {
        label: "Second team",
        summary: "Recorded against the request, but nothing anywhere acts on it.",
        detail: [
          "This value is written onto the request when the rule fires and it can be read back out again, but no notification, no queue, no status change and no permission check anywhere in the system consults it. Whatever you set here, the same things happen.",
          "The values that appear in this list — business, legal operations, or nothing — all produce identical behaviour, because none of them is ever looked at.",
          "It is being reported rather than quietly explained away: this is a field on a live settings screen that does not do anything.",
        ],
        fallback: "Blank.",
        status: "inert",
        statusNote: "Stored and displayed. Never acted on.",
      },

      // src: same as secondary_route for the runtime half. The one real effect is
      // services/provisioningDefaults.js:381 and :383 (promotion / skip).
      secondary_role: {
        label: "Second team's role",
        summary:
          "Nothing acts on it in a running system. It matters only while a customer is being created.",
        detail: [
          "Like the second team above, this value is written onto the request and then never read. No message, queue, status or permission depends on it.",
          "Its one genuine effect happens while a new customer is being set up. If the main role is blank, this value is promoted into the main role's place and the rule is copied with the second-team role cleared. If both are blank, the rule is not copied to that customer at all, and the omission is reported rather than passed over.",
        ],
        fallback: "Blank.",
        interacts: "The role named in the alert, which it stands in for when that is blank.",
        status: "inert",
        statusNote: "No runtime effect; used only while a customer is being created.",
      },

      // src: every consumer filters on it — routes/requests.js:862, :902, :2212;
      // routes/selfService.js:1396; routes/routingEngine.js:55;
      // routes/settings.js:586.
      is_active: {
        label: "Active",
        summary: "Whether the rule does anything in the customer's live system.",
        detail: [
          "Every place that consults routing rules asks only for active ones.",
          "On: a request of this agreement type notifies the owning team and lands in that team's queue.",
          "Off: the rule is invisible. A request of that type notifies nobody and waits in the general unassigned queue for a person to pick up. Nothing else changes, and no warning is shown to the requester.",
          "Turning it back on affects requests submitted from then onward. Requests already in flight keep the routing they were given.",
        ],
        fallback: "Active. A rule with nothing recorded either way is treated as active.",
        status: "live",
      },

      selected: SELECTED,
    },
  },

  // ───────────────────────────────────────────────────────────────────────────
  draft_upload_rules: {
    label: "Draft upload rules",
    summary:
      "What happens when a draft is uploaded: what the request's status becomes, who is allowed to trigger it, and who is told.",
    detail: [
      "There is one rule for each of the four kinds of draft. When a draft of that kind is uploaded, the matching rule decides the request's new status, checks whether the person uploading is permitted to cause that change, and sends the messages.",
      "If a rule is switched off, the behaviour does not stop — the system falls back to a fixed behaviour built into the product. Switching a rule off therefore restores the original hard-wired conduct rather than disabling it.",
    ],
    fields: {
      // src: utils/draftStatusHandler.js:500-585 — one branch per label, plus
      // `default: return null` at :583. Valid labels list at
      // routes/draftUploadRules.js:6-9. Call sites: routes/versions.js:339, :506,
      // :816; routes/messageAttachments.js:212; services/inboundEmail.js:2387.
      draft_label: {
        label: "Kind of draft",
        summary: "Which of the four kinds of upload this rule governs.",
        detail: [
          "The system handles exactly four kinds of upload, and each has its own rule. A rule carrying any other name is never consulted by anything.",
          "The four are: the internal working version, the version prepared to send out to the other side, the version the other side has sent back, and the version approved for signature.",
        ],
        values: [
          {
            value: "Internal draft",
            effect:
              "Uploaded from the app or the Word add-in. With no active rule, the request moves to internal review pending and the requester is emailed.",
          },
          {
            value: "Company draft",
            effect:
              "Our own version, ready to go out. With no active rule, the request moves to counterparty draft ready and the requester is emailed with the file attached, asked to send it on, and told they can reply \"SENT\" to move the request forward.",
          },
          {
            value: "Counterparty draft",
            effect:
              "The version the other side returned. It can also arrive automatically from a reply to one of the system's own emails. With no active rule, the request moves to legal review, or to commercial review if the request is owned by commercial operations.",
          },
          {
            value: "Final draft",
            effect:
              "Created by the separate finalise action, not by an ordinary upload. With no active rule, the request moves to approved for signature, and only administrators, lawyers, contract managers, legal operations and commercial operations may cause it.",
          },
        ],
        fallback:
          "The four are fixed. There is no default, and adding a fifth would have no effect.",
        status: "live",
      },

      // src: utils/draftStatusHandler.js:87 (returned), :587-589 (applied only if
      // truthy). Free text by design — migrations/063_draft_upload_rules.sql:13-15.
      // Seeded values at 063:48-51.
      target_status: {
        label: "New status",
        summary: "The status the request moves to when a draft of this kind is uploaded.",
        detail: [
          "On upload, the request is moved to this status and a line is added to its history saying so.",
          "The value is free text and is not checked against the list of statuses the product actually uses. A value that is not a real status will be written to the request and no screen will filter on it, so the request effectively disappears from the working lists. There is no warning.",
          "If it is left blank, the request's status is not changed at all — but a line is still added to its history saying it was, which will read as though something happened.",
        ],
        fallback:
          "As first supplied: internal review pending, counterparty draft ready, legal review, and approved for signature, in the order of the four kinds above. Those four mirror exactly what the product did before these rules existed.",
        interacts:
          "The follow-the-team switch below, which can substitute the alternative status in its place.",
        status: "live",
      },

      // src: utils/draftStatusHandler.js:86-97 (determineStatusFromRule).
      // Default false at migrations/063_draft_upload_rules.sql:17 and the
      // COALESCE at services/provisioningDefaults.js:396.
      routing_aware: {
        label: "Follow the owning team",
        summary:
          "Whether the status above can be overridden for a request owned by commercial operations.",
        detail: [
          "On: if the request is owned by commercial operations, the alternative status below is used instead. If it is owned by legal, or was never routed by a rule at all, the ordinary status is used.",
          "Off: the ordinary status is always used, whichever team owns the request.",
        ],
        fallback: "Off. Only the counterparty-draft rule is supplied with it turned on.",
        interacts:
          "The owning team on the routing rule that matched the request when it was submitted. If no routing rule matched, there is no owning team recorded and this switch changes nothing.",
        status: "live",
      },

      // src: utils/draftStatusHandler.js:93 — returned only when routing_aware is
      // true AND the stored route is commercial_ops. A null return then falls
      // through the `if (newStatus)` guard at :587.
      routing_alt_status: {
        label: "Alternative status for commercial requests",
        summary: "The status used instead when the request is owned by commercial operations.",
        detail: [
          "Only consulted when the follow-the-team switch above is on.",
          "If that switch is on and this is left blank, a commercial request's status is not changed at all when the draft is uploaded — while its history still records a change to the ordinary status. That combination is a trap: it looks configured and it silently does nothing.",
          "Ignored entirely when the switch is off, whatever is in it.",
        ],
        fallback:
          "Blank. The counterparty-draft rule is supplied with the commercial review status here.",
        interacts: "The follow-the-team switch, without which it is never read.",
        status: "live",
      },

      // src: utils/draftStatusHandler.js:80-84 (isAuthorizedForRule) — a blank or
      // missing value returns true for EVERYONE; a populated value is split on
      // commas and matched exactly against the uploader's role. Enforcement sites
      // :503, :520, :537, :564. The final-draft-only built-in list at :568.
      // The 'external' caller is services/inboundEmail.js:2387.
      authorized_roles: {
        label: "Who may trigger this",
        summary:
          "A comma-separated list of job roles. Leaving it blank permits everybody, not nobody.",
        detail: [
          "This is the field most likely to be read backwards, so it is stated plainly: blank means everyone. If the list is empty or missing, every signed-in person passes the check, whatever their role.",
          "If the list has entries, the uploader's role must appear in it exactly. Somebody whose role is not named still has their file stored, but the request's status does not change and no messages are sent. They are not told why.",
          "One of the callers is not a person. When the other side replies to one of the system's emails with a revised draft attached, the system records that upload under the role name \"external\". If this list names roles on the counterparty-draft rule, that email route silently stops moving requests forward, because \"external\" will never be in the list.",
          "The list only applies while the rule is active. If the final-draft rule is switched off, a fixed built-in list takes over for final drafts — administrators, lawyers, contract managers, legal operations and commercial operations. The other three kinds have no such fallback: with their rule switched off, anybody may trigger them.",
        ],
        fallback:
          "As first supplied, only the final-draft rule named roles and the other three were blank. What is stored here now is whatever the customer this list was captured from had.",
        interacts:
          "The active switch, which changes what happens when the rule is off — restrictive for final drafts, permissive for the rest.",
        status: "live",
      },

      // src: utils/draftStatusHandler.js:179-193 — early return when falsy;
      // hardcodedNotificationFallback at :192 when the configured list is empty.
      // Default true at migrations/063_draft_upload_rules.sql:28.
      notify_on_transition: {
        label: "Send messages",
        summary: "Whether anybody is emailed or notified when a draft of this kind is uploaded.",
        detail: [
          "On: the messages configured against this rule are sent. If no messages have been configured against it, a fixed built-in set is sent instead and a warning is recorded for the operator to look into.",
          "Off: nothing is sent to anybody, through any channel. The status change still happens and the request's history is still written — the change simply happens silently.",
        ],
        fallback: "On.",
        status: "live",
      },

      // src: utils/draftStatusHandler.js:71 (`AND is_active = true` inside
      // resolveDraftRule) then the `rule ? … : fallback` branches at :507, :524,
      // :550, :572, and the final-draft-only role floor at :568.
      is_active: {
        label: "Active",
        summary:
          "Whether this rule is used. Switching it off does not stop the behaviour — it reverts to the built-in one.",
        detail: [
          "Only active rules are found. This is the point most likely to surprise: an inactive rule does not mean nothing happens. It means the system falls back to what it did before these rules were configurable.",
          "With the rule off, the built-in behaviour applies: the request still changes status, messages are still sent, and for final drafts a fixed role restriction still applies. Any custom status, custom recipients or custom role list on the rule is simply ignored.",
        ],
        fallback: "Active.",
        interacts:
          "Who may trigger this, and the new status — both are what the fallback replaces when the rule is off.",
        status: "live",
      },

      selected: SELECTED,
    },
  },

  // ───────────────────────────────────────────────────────────────────────────
  parent_requirements: {
    label: "Parent agreement requirements",
    summary:
      "Which agreement types cannot stand alone — they must be linked to a parent agreement — and which agreements count as a valid parent.",
    detail: [
      "When somebody starts a request, the system checks these rules against the category and the agreement type they picked. If a rule matches, the intake form stops and asks them to link a parent before they can submit.",
      "The same rules are enforced again later, when somebody links or changes a parent on an existing request, and are used during bulk import of existing contracts to detect and correct a parent of the wrong type.",
    ],
    fields: {
      // src: routes/parentRules.js:107-116 (the "Amendment to " collapse and the
      // exact lookup), routes/requests.js:2062-2067 (same collapse on relink),
      // frontend IntakeModal.tsx:383-385 and :564 (the submit block).
      child_type: {
        label: "Agreement type that needs a parent",
        summary: "The type of agreement that cannot be requested on its own.",
        detail: [
          "When somebody picks this type, in this category, on a new request, the intake form asks them to link a parent and will not let them submit until they either pick one, say the parent is a set of online terms if that is permitted, or write an explanation of why there is no parent.",
          "Anything whose type begins with \"Amendment to \" is looked up as plain \"Amendment\" instead, so a single Amendment rule covers every kind of amendment without needing one rule each. Everything else is matched exactly as written.",
        ],
        fallback: "No default — a rule without one matches nothing.",
        interacts:
          "The category, which must also match. The same agreement type can require different parents in different categories.",
        status: "live",
      },

      // src: the ONLY behavioural read is utils/extractionWorker.js:1979, which
      // stamps it onto a corrected parent link during bulk import. It is also
      // returned to the intake form at routes/parentRules.js:300, where nothing
      // in the frontend reads it (checked across IntakeModal.tsx and
      // RequestDetail.tsx). Default 'child' at
      // migrations/002b_schema_reconciliation.sql:132.
      relationship_type: {
        label: "Kind of link",
        summary:
          "The word recorded on a parent link that the bulk importer corrects. It does not affect the requirement itself.",
        detail: [
          "Its only real effect is during bulk import of existing contracts. If the system decides that the parent picked automatically for a document was the wrong type, it substitutes a better one and records the link using the word set here.",
          "It is sent to the intake form as part of the rule, but nothing on the intake form reads it. It does not decide whether a parent is required, which parents are allowed, or what is done with the request.",
        ],
        values: [
          {
            value: "Child",
            effect: "Corrected links are labelled as a child of the parent.",
          },
          {
            value: "Amendment to parent",
            effect: "Corrected links are labelled as an amendment. Nothing else differs.",
          },
          {
            value: "Change order",
            effect: "Corrected links are labelled as a change order. Nothing else differs.",
          },
        ],
        fallback: "Child.",
        status: "live",
        statusNote: "Live only during bulk import of existing contracts.",
      },

      // src: routes/parentRules.js:113-115 and routes/requests.js:2066 — both
      // require an exact category match alongside child_type. The provisioning
      // gate is services/provisioningDefaults.js:336.
      agreement_category: {
        label: "Category",
        summary: "Which of the three request categories the rule belongs to.",
        detail: [
          "A rule only fires when both the category and the agreement type on the request match it. The three categories are corporate, customer and vendor.",
          "This makes it possible for the same agreement type to require a different parent depending on which side of the business the request comes from.",
        ],
        fallback: "No default — a rule without one matches nothing.",
        interacts:
          "The contract type categories a new customer is being given. While a customer is being created, a rule is only copied if that customer is also receiving the contract types for its category. A rule for a category they are not getting is dropped.",
        status: "live",
      },

      // src: read at routes/parentRules.js:301, consumed at frontend
      // IntakeModal.tsx:98-103 (the extra radio) and RequestDetail.tsx:766. The
      // downstream effect of NOT choosing it is routes/requests.js:658
      // (explanation → needs_more_information).
      online_terms_eligible: {
        label: "Online terms may stand in for a parent",
        summary:
          "Whether the requester may answer that the parent is a published set of online terms instead of naming a document.",
        detail: [
          "On: an extra choice appears on the intake form saying the parent agreement is governed by online terms. Choosing it satisfies the parent requirement. The requester then says whose terms govern — the other side's or our own — and may paste a link, though the link is not required at that point; the attorney adds it before signature.",
          "Off: that choice is not offered. The requester must either pick a parent from the list the system found, or write an explanation of why there is no parent. Writing an explanation submits the request flagged as needing more information, which puts it in front of a person rather than into the normal queue.",
        ],
        fallback: "Off.",
        interacts:
          "The list of allowed parent types — the online terms answer is the escape route when no document of an allowed type exists.",
        status: "live",
      },

      // src: routes/parentRules.js:126 onwards (candidate lists are filtered to
      // these types), routes/requests.js:2082-2086 (the relink refusal),
      // utils/extractionWorker.js:1873 and :1888 (bulk-import correction),
      // routes/parentRules.js:327-331 (the suggest-children lookup, reversed).
      // The provisioning drop is services/provisioningDefaults.js:340-343.
      required_parent_types: {
        label: "Agreements that count as a parent",
        summary: "The list of agreement types that may be linked as the parent.",
        detail: [
          "It does three things. It fills the list of candidate parents the requester chooses from — only existing contracts and in-flight requests of these types are offered. It is enforced afterwards: linking a parent whose type is not on this list is refused outright, with a message naming the types that are allowed. And during bulk import it is what tells the system that an automatically chosen parent was the wrong type and should be replaced.",
          "It is also read in reverse. When somebody creates one of a small set of umbrella agreements, the system looks for rules that name that umbrella as an allowed parent, and offers to start the related child requests straight away.",
          "An empty list makes the requirement impossible to satisfy: nothing would ever be offered and every link would be refused. While a customer is being created, a rule whose allowed parent types all fall away because that customer is not receiving them is dropped and reported, rather than copied empty.",
        ],
        fallback: "No default — a rule must name at least one.",
        status: "live",
      },

      // src: filtered at routes/parentRules.js:35, :115, :329;
      // routes/requests.js:2066; utils/extractionWorker.js:1847.
      is_active: {
        label: "Active",
        summary: "Whether the requirement is enforced at all.",
        detail: [
          "Every place that consults these rules asks only for active ones.",
          "Off: no parent is required for that agreement type. The intake form does not ask, a parent of any type may be linked afterwards without objection, and the bulk importer applies no correction to documents of that type.",
        ],
        fallback: "Active.",
        status: "live",
      },

      selected: SELECTED,
    },
  },

  // ───────────────────────────────────────────────────────────────────────────
  password_policy: {
    label: "Password policy",
    summary:
      "The password rules a new customer starts with. Their own administrators can change them afterwards.",
    detail: [
      "These four values are written into the new customer's own settings when they are created. They are a starting point, not a standing control: the customer's administrators can change every one of them from their own administration screen, and editing them here does not change any customer who already exists.",
      "They are checked in three places in the customer's system — when somebody sets a password for the first time from an invitation, when somebody changes their own password, and when an administrator resets somebody else's. They are not checked anywhere else.",
      "If this whole area is excluded when a customer is created, nothing is written and the customer falls back to the same values anyway: a minimum of eight characters with none of the three requirements turned on.",
    ],
    fields: {
      // src: utils/passwordPolicy.js:38 (`Math.max(4, parseInt(...) || 8)`) and
      // :74-76 (the check). Enforced at routes/auth.js:384,
      // routes/invites.js:87, routes/_combined.js:318.
      min_length: {
        label: "Minimum length",
        summary: "The shortest password the customer's system will accept.",
        detail: [
          "A password shorter than this is refused, with a message stating the minimum.",
          "Anything below four is silently raised to four — the system will not run a policy weaker than that, whatever is set here. A value that is missing or not a number is treated as eight.",
          "There is a short delay before a change takes effect in a running system: the value is remembered for up to a minute at a time.",
        ],
        fallback:
          "Eight. It is the value the system uses when nothing has been configured, so it is what a customer would get whether or not this area is copied to them.",
        interacts:
          "The administrator's own first password. The person set up as the new customer's administrator is never given a password here — they receive an invitation and choose one, and that choice is checked against this policy.",
        status: "live",
      },

      // src: utils/passwordPolicy.js:40 and :80-82 (`/[0-9]/`).
      require_digit: {
        label: "Require a digit",
        summary: "Whether every password must contain at least one number.",
        detail: [
          "On: a password with no digit in it is refused, with a message saying a digit is required.",
          "Off: digits are permitted but not required.",
        ],
        fallback:
          "Off. A customer with nothing configured has it off, so off is what the system does by default and what this mirrors.",
        status: "live",
      },

      // src: utils/passwordPolicy.js:39 and :77-79 (`/[A-Z]/`).
      require_upper: {
        label: "Require a capital letter",
        summary: "Whether every password must contain at least one capital letter A to Z.",
        detail: [
          "On: a password with no capital letter is refused.",
          "Off: capital letters are permitted but not required.",
          "The check covers the plain English capitals A to Z only. An accented or non-Latin capital does not satisfy it, so a password that plainly contains a capital to the person typing it can still be refused.",
        ],
        fallback: "Off, for the same reason as the digit requirement.",
        status: "live",
      },

      // src: utils/passwordPolicy.js:41 and :83-85 (`/[^A-Za-z0-9]/`).
      require_special: {
        label: "Require a special character",
        summary:
          "Whether every password must contain at least one character that is not a letter or a number.",
        detail: [
          "On: a password made only of letters and digits is refused. Anything else counts — punctuation, a symbol, a space, or an accented letter.",
          "Off: such characters are permitted but not required.",
        ],
        fallback: "Off, for the same reason as the digit requirement.",
        status: "live",
      },
    },
  },
};

/** Section keys this module covers, in the order the console lists them. */
export const HELPED_SECTIONS = Object.keys(SETTINGS_HELP);

export function sectionHelp(section: string): SectionHelp | null {
  return SETTINGS_HELP[section] || null;
}

export function fieldHelp(section: string, field: string): FieldHelp | null {
  return SETTINGS_HELP[section]?.fields[field] || null;
}

/** Short label for the status badge shown beside a field name. */
export const STATUS_LABEL: Record<FieldStatus, string> = {
  live: "In use",
  setup_only: "Set-up only",
  inert: "No effect",
  undetermined: "Unknown",
};

export const STATUS_TONE: Record<FieldStatus, "ok" | "info" | "warn" | "err"> = {
  live: "ok",
  setup_only: "info",
  inert: "warn",
  undetermined: "err",
};
